import { z } from 'zod';

import { mcqOptionPublicSchema, questionDifficultySchema } from './question';
import { rubricJudgementSchema } from './scoring';

/**
 * Practice session API contract (PRE-18, System Design §8).
 *
 * Response field names are snake_case to match the documented session endpoint
 * contract the mobile app consumes (Notion PRE-18 §3).
 */

// Session kind — free-text `session_type` column accepts these values.
// `graduation` (PRE-134/BR-72) is an exit-assessment re-measuring a completed
// roadmap's target skills; it is created via POST /v1/roadmaps/:id/graduation,
// not the generic POST /v1/sessions, so it is intentionally excluded from
// createSessionInputSchema below.
export const sessionTypeSchema = z.enum([
  'baseline_practice',
  'roadmap_practice',
  'dependency',
  'graduation',
  // PRE-98: the assessment types. `POST /v1/sessions` opens one of these as an
  // assessment (`assessments` + `assessment_items`) and returns it in this same
  // response shape — one flow on the wire, two stores underneath, which is the
  // split `lib/assessment-flow.ts` explains. A client asks for the kind of
  // session it wants and never learns where the rows went.
  'baseline',
  'ai_dependency',
]);
export type SessionType = z.infer<typeof sessionTypeSchema>;

/**
 * POST /v1/sessions body. `graduation` is created via the roadmap route only.
 *
 * PRE-98: `baseline` / `ai_dependency` open an assessment. `skillId` and
 * `roadmapItemId` are meaningless there — the assessment picks its own
 * questions across skills (BR-87) — and are ignored rather than rejected, for
 * the same reason PRE-100 ignores a client-sent `items`.
 */
export const createSessionInputSchema = z.object({
  sessionType: sessionTypeSchema.exclude(['graduation']),
  skillId: z.string().min(1).optional(),
  roadmapItemId: z.string().min(1).optional(),
});
export type CreateSessionInput = z.infer<typeof createSessionInputSchema>;

/** POST /v1/sessions/:id/answers body (Idempotency-Key comes from the header). */
export const submitAnswerInputSchema = z
  .object({
    sessionItemId: z.string().min(1),
    answerText: z.string().optional(),
    answerCode: z.string().optional(),
    /** Client-measured time on the item, ms. */
    durationMs: z.number().int().min(0),
    confidence: z.number().int().min(1).max(5).optional(),
    /**
     * PRE-128 / BR-25: client-reported hint ladder depth on assessment answers.
     * Practice still records hints via `POST .../hints` rows; ignored there.
     */
    hintCount: z.number().int().min(0).optional(),
  })
  .refine((v) => Boolean(v.answerText) || Boolean(v.answerCode), {
    message: 'answerText or answerCode is required',
    path: ['answerText'],
  });
export type SubmitAnswerInput = z.infer<typeof submitAnswerInputSchema>;

/** POST /v1/sessions/:id/hints body. */
export const hintInputSchema = z.object({
  sessionItemId: z.string().min(1),
});
export type HintInput = z.infer<typeof hintInputSchema>;

// ── Response shapes ─────────────────────────────────────────────────────────

export const sessionItemViewSchema = z.object({
  session_item_id: z.string(),
  question_id: z.string(),
  type: z.string(),
  prompt: z.string(),
  order_index: z.number().int(),
  status: z.string(),
  /** Number of hint levels the question offers (from the seed ladder). */
  hints_available: z.number().int(),
  /**
   * PRE-166 / PRE-158: IRT-lite selected difficulty for the visible practice chip.
   * Defaults to medium when the bank row is missing.
   */
  difficulty: questionDifficultySchema,
  /**
   * BR-59 / PRE-179: public MCQ choices for practice UI (`correct` never included).
   * Null/omitted for non-mcq or when options are unavailable.
   */
  options: z.array(mcqOptionPublicSchema).nullable().optional(),
  /**
   * PRE-128: seed hint ladder text for assessment UI (client reveal; `/hints` is
   * 409 on assessment sessions). Optional so practice payloads stay lean.
   */
  hints: z.array(z.string()).optional(),
  /**
   * PRE-128: concepts for client `POST /v1/feedback` during baseline. Optional
   * on practice items (server scoring uses the bank row directly).
   */
  expectedConcepts: z.array(z.string()).optional(),
});
export type SessionItemView = z.infer<typeof sessionItemViewSchema>;

export const sessionResponseSchema = z.object({
  session_id: z.string(),
  status: z.string(),
  session_type: z.string(),
  current_step: z.number().int(),
  current_item: sessionItemViewSchema.nullable(),
  items: z.array(sessionItemViewSchema),
  /** ISO timestamp of last user activity (drives soft-timeout + resume expire). */
  last_activity_at: z.string().optional(),
  /**
   * True when status is PAUSED/ABANDONED and still inside the resume window
   * (BR-49 / PRE-177). Clients use this to gate Continue / Resume CTAs.
   * Optional for older API payloads during rollout.
   */
  resumable: z.boolean().optional(),
});
export type SessionResponse = z.infer<typeof sessionResponseSchema>;

export const hintResponseSchema = z.object({
  session_item_id: z.string(),
  hint_level: z.number().int(),
  hint_text: z.string(),
  next_action: z.enum(['answer', 'reflect', 'continue', 'complete']),
});
export type HintResponse = z.infer<typeof hintResponseSchema>;

/** One heuristic feedback report for an answered item within a session. */
export const sessionFeedbackReportSchema = z.object({
  id: z.string(),
  question_id: z.string().nullable(),
  scoring_strategy: z.string(),
  total_score: z.number(),
  concept_mastery_score: z.number(),
  explanation_score: z.number(),
  ai_dependency_score: z.number(),
  strengths: z.array(z.string()),
  weaknesses: z.array(z.string()),
  feedback_text: z.string(),
  /** LLM rubric judgement when the judge ran; null for heuristic-only reports. */
  rubric: rubricJudgementSchema.nullable().default(null),
  created_at: z.string(),
});
export type SessionFeedbackReport = z.infer<typeof sessionFeedbackReportSchema>;

export const sessionFeedbackResponseSchema = z.object({
  session_id: z.string(),
  status: z.string(),
  reports: z.array(sessionFeedbackReportSchema),
  /**
   * True while scoring is still running *and* this session will write a new
   * roadmap before becoming SCORED (bootstrap / placement). Mobile shows the
   * plan-building screen instead of a generic feedback spinner.
   */
  building_plan: z.boolean().optional(),
});
export type SessionFeedbackResponse = z.infer<typeof sessionFeedbackResponseSchema>;
